import { useEffect, useRef, useState } from 'react'
import { OnboardingStep } from '@/hooks/use-onboarding'
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  useAnswerFeedbackPrompt,
  useDismissFeedbackPrompt,
  usePendingFeedbackPrompts,
  type FeedbackPrompt,
} from '@/hooks/use-feedback'
import { OnboardingStepNext, runOnboardingStepNext } from '@/lib/onboarding-step-nav'

interface TeamFeedbackStepProps {
  step: OnboardingStep
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

/**
 * Optional end-of-flow panel: answers pending onboarding-scoped FeedbackPrompts
 * (admin-authored). Skip or dismiss when there is nothing to answer.
 */
export function TeamFeedbackStep({
  step,
  onNext,
  onBack,
  isCompleting,
  canGoBack,
}: TeamFeedbackStepProps) {
  const { prompts, isLoading } = usePendingFeedbackPrompts('onboarding')
  const { answerPrompt, isAnswering } = useAnswerFeedbackPrompt()
  const { dismissPromptAsync, isDismissing } = useDismissFeedbackPrompt()
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const autoAdvanced = useRef(false)

  // No active prompts → auto-advance once loaded (step is optional).
  useEffect(() => {
    if (isLoading || isCompleting || saving || autoAdvanced.current) return
    if (prompts.length === 0) {
      autoAdvanced.current = true
      void runOnboardingStepNext(onNext, setError)
    }
  }, [isLoading, prompts.length, isCompleting, saving, onNext])

  const setAnswer = (promptId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [promptId]: value }))
  }

  const handleSkip = async () => {
    setSaving(true)
    setError(null)
    try {
      for (const prompt of prompts) {
        await dismissPromptAsync(prompt.id)
      }
      await runOnboardingStepNext(onNext, setError)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to skip')
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      for (const prompt of prompts) {
        const text = (answers[prompt.id] || '').trim()
        if (!text) {
          await dismissPromptAsync(prompt.id)
          continue
        }
        await answerPrompt({
          promptId: prompt.id,
          title: text.length <= 120 ? text : `${text.slice(0, 117)}...`,
          body: text,
          kind: prompt.kind_hint ?? 'onboarding',
        })
      }
      await runOnboardingStepNext(onNext, setError)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save feedback')
    } finally {
      setSaving(false)
    }
  }

  if (isLoading || prompts.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          {isLoading ? 'Loading…' : 'Nothing to ask right now — continuing.'}
        </p>
      </div>
    )
  }

  const busy = saving || isAnswering || isDismissing || isCompleting

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <p className="text-muted-foreground">
          {step.description ||
            'Optional: tell the team how setup went. Answers stay private to platform operators.'}
        </p>
        {prompts.map((prompt: FeedbackPrompt) => (
          <div key={prompt.id} className="space-y-2">
            <Label htmlFor={`onboarding-prompt-${prompt.id}`}>{prompt.question}</Label>
            <Textarea
              id={`onboarding-prompt-${prompt.id}`}
              value={answers[prompt.id] ?? ''}
              onChange={(e) => setAnswer(prompt.id, e.target.value)}
              disabled={busy}
              rows={3}
              placeholder="Your answer (optional)"
            />
          </div>
        ))}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </div>
      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        isBusy={busy}
        backDisabled={busy}
        continueDisabled={busy}
        trailing={
          <Button type="button" variant="ghost" disabled={busy} onClick={() => void handleSkip()}>
            Skip
          </Button>
        }
        onContinue={() => void handleSave()}
        continueLabel="Continue"
      />
    </div>
  )
}
